import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MessageCircle, X, Send, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOMMY_IMAGE = "https://customer-assets.emergentagent.com/job_agent-jobs-hub/artifacts/8k1sk2pq_359.jpg";

export const TommyChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hey there! I'm Tommy, your friendly neighborhood tuxedo cat 🐱 How can I help you navigate TripleTC today?"
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const response = await axios.post(`${API}/chat/tommy`, {
        message: userMessage,
        session_id: sessionId
      });

      setSessionId(response.data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
    } catch (error) {
      // Fallback responses when AI is unavailable
      const fallbackResponses = [
        "Hey! I'm Tommy, the TripleTC mascot. For quick help: Browse AI agents at /agents, post jobs at /jobs, or check out Premium for 4% fees! Email support@tripletc.com for more help.",
        "Meow! While my AI brain takes a catnap, here's what you need: TripleTC connects businesses with AI agents. 15% fee (or 4% with Premium). We accept Card, Google Pay & Crypto!",
        "Tommy here! Quick answers: Browse agents, post jobs, or subscribe to Premium ($10/mo) for lower fees. Questions? Email support@tripletc.com!"
      ];
      const randomResponse = fallbackResponses[Math.floor(Math.random() * fallbackResponses.length)];
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: randomResponse
      }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Chat Button */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            onClick={() => setIsOpen(true)}
            className="fixed bottom-6 right-6 z-50 w-16 h-16 rounded-full bg-gradient-to-r from-indigo-500 to-cyan-500 shadow-[0_0_30px_rgba(99,102,241,0.4)] flex items-center justify-center hover:scale-110 transition-transform"
            data-testid="tommy-chat-button"
            aria-label="Open chat with Tommy, our AI support assistant"
          >
            <div className="relative">
              <img 
                src={TOMMY_IMAGE} 
                alt="" 
                aria-hidden="true"
                className="w-12 h-12 rounded-full object-cover border-2 border-white"
              />
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white" aria-label="Online" />
            </div>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-0 right-0 sm:bottom-6 sm:right-6 z-50 w-full sm:w-[380px] h-[100dvh] sm:h-[500px] bg-zinc-900 border-t sm:border border-white/10 sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden"
            data-testid="tommy-chat-window"
            role="dialog"
            aria-label="Chat with Tommy"
            aria-modal="true"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-500/20 to-cyan-500/20 border-b border-white/10 p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <img 
                    src={TOMMY_IMAGE} 
                    alt="Tommy" 
                    className="w-10 h-10 rounded-full object-cover border-2 border-cyan-500/50"
                  />
                  <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-zinc-900" />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-sm">Tommy</h3>
                  <p className="text-cyan-400 text-xs">AI Support • Online</p>
                </div>
              </div>
              <button 
                onClick={() => setIsOpen(false)}
                className="text-zinc-400 hover:text-white transition-colors"
                aria-label="Close chat"
              >
                <X className="w-5 h-5" aria-hidden="true" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((msg, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  {msg.role === 'assistant' && (
                    <img 
                      src={TOMMY_IMAGE} 
                      alt="Tommy" 
                      className="w-8 h-8 rounded-full object-cover mr-2 flex-shrink-0"
                    />
                  )}
                  <div
                    className={`max-w-[75%] px-4 py-2.5 rounded-2xl ${
                      msg.role === 'user'
                        ? 'bg-indigo-500 text-white rounded-br-md'
                        : 'bg-zinc-800 text-zinc-200 rounded-bl-md'
                    }`}
                  >
                    <p className="text-sm leading-relaxed">{msg.content}</p>
                  </div>
                </motion.div>
              ))}
              {loading && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex items-center gap-2"
                >
                  <img 
                    src={TOMMY_IMAGE} 
                    alt="Tommy" 
                    className="w-8 h-8 rounded-full object-cover"
                  />
                  <div className="bg-zinc-800 px-4 py-2.5 rounded-2xl rounded-bl-md">
                    <div className="flex items-center gap-1">
                      <span className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </motion.div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <form onSubmit={sendMessage} className="p-4 border-t border-white/10">
              <div className="flex gap-2">
                <Input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask Tommy anything..."
                  className="flex-1 bg-zinc-800 border-white/10 focus:border-cyan-500/50 text-sm"
                  disabled={loading}
                  data-testid="tommy-chat-input"
                  aria-label="Type your message to Tommy"
                />
                <Button 
                  type="submit" 
                  disabled={loading || !input.trim()}
                  className="bg-cyan-500 hover:bg-cyan-600 text-white rounded-xl px-3"
                  data-testid="tommy-chat-send"
                  aria-label={loading ? "Sending message" : "Send message"}
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <Send className="w-4 h-4" aria-hidden="true" />}
                </Button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default TommyChat;
